import PageEditSessionRecord from "models/view-models/page-edit-session-record";
import usePageEditSessions from "pages/notebooks/use-page-edit-sessions";
import React, {
    PropsWithChildren,
    createContext,
    useContext,
    useEffect,
    useRef,
} from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useCurrentIdentity from "utilities/hooks/use-current-identity";

// -------------------------------------------------------------------------------------------------
// #region Types
// -------------------------------------------------------------------------------------------------

interface PageEditSessionContextInterface {
    activeEditSessions: PageEditSessionRecord[];
    startEditSession: (blockId: number) => Promise<boolean>;
    updateEditSession: (
        sessionId: number,
        blockId?: number
    ) => Promise<boolean>;
    endEditSession: (sessionId: number) => Promise<boolean>;
    refreshSessionList: () => void;
    pageId?: number;
    isInEditMode: boolean;
}

// #endregion Types

// -------------------------------------------------------------------------------------------------
// #region Context
// -------------------------------------------------------------------------------------------------

const defaultUpdater = async (blockId: number) => false;

export const defaultState = {
    activeEditSessions: [],
    startEditSession: defaultUpdater,
    updateEditSession: defaultUpdater,
    endEditSession: defaultUpdater,
    refreshSessionList: () => {},
    pageId: undefined,
    isInEditMode: false,
};

export const PageEditSessionContext =
    createContext<PageEditSessionContextInterface>(defaultState);

// #endregion Context

// -------------------------------------------------------------------------------------------------
// #region Provider
// -------------------------------------------------------------------------------------------------
interface PageEditSessionContextProviderProps extends PropsWithChildren {
    pageId?: number;
    isInEditMode: boolean;
    isPollingEnabled: boolean;
}
const PageEditSessionContextProvider = (
    props: PageEditSessionContextProviderProps
) => {
    // Sessions for each block
    const { pageId, isInEditMode, isPollingEnabled } = props;

    const {
        activeEditSessions,
        startEditSession,
        updateEditSession,
        endEditSession,
        refreshSessionList,
    } = usePageEditSessions(isPollingEnabled, pageId);

    return (
        <PageEditSessionContext.Provider
            value={{
                activeEditSessions,
                startEditSession,
                updateEditSession,
                endEditSession,
                pageId,
                isInEditMode,
                refreshSessionList,
            }}>
            {props.children}
        </PageEditSessionContext.Provider>
    );
};

// #endregion Provider

// -------------------------------------------------------------------------------------------------
// #region Hook
// -------------------------------------------------------------------------------------------------
interface UsePageEditSessionContextOptions {
    handleBlocksChangedByAnotherUser?: () => void;
}

export const usePageEditSessionContext = ({
    handleBlocksChangedByAnotherUser,
}: UsePageEditSessionContextOptions = {}) => {
    const {
        activeEditSessions,
        endEditSession,
        startEditSession,
        updateEditSession,
        pageId,
        isInEditMode,
        refreshSessionList,
    } = useContext(PageEditSessionContext);
    const { currentIdentity } = useCurrentIdentity();
    const activePageEditSession = pageId
        ? activeEditSessions.find((a) => a.pageId === pageId && !a.blockId)
        : undefined;
    const getSessionForBlock = (blockId?: number) => {
        if (!blockId) return undefined;
        return activeEditSessions.find(
            (a) => !!a.blockId && a.blockId === blockId
        );
    };

    const currentUserActiveSession = activeEditSessions.find(
        (a) => a.userId === currentIdentity?.userId()
    );

    const currentUserActiveSessionId = currentUserActiveSession?.id;

    useEffect(() => {
        if (!isInEditMode && !!currentUserActiveSessionId) {
            endEditSession(currentUserActiveSessionId);
        }
    }, [currentUserActiveSessionId, endEditSession, isInEditMode]);

    const previousActiveSessions = useRef(activeEditSessions);

    // If prev sessions and current sessions are different, a block has been changed by another user, and we need to refetch the blocks
    useEffect(() => {
        if (
            previousActiveSessions.current &&
            activeEditSessions &&
            !CollectionUtils.equalsBy(
                (p) => p.blockId,
                previousActiveSessions.current,
                activeEditSessions
            ) &&
            handleBlocksChangedByAnotherUser
        ) {
            handleBlocksChangedByAnotherUser();
            previousActiveSessions.current = activeEditSessions;
        }
    }, [activeEditSessions, currentIdentity, handleBlocksChangedByAnotherUser]);

    return {
        activeEditSessions,
        activePageEditSession,
        endEditSession,
        startEditSession,
        updateEditSession,
        getSessionForBlock,
        currentUserActiveSession,
        refreshSessionList,
    };
};

// #endregion Hook

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default PageEditSessionContextProvider;

// #endregion Exports
